import React, { useEffect, useState } from "react";
import {
  Avatar,
  Box,
  Grid2,
  Link,
  List,
  ListItem,
  ListItemAvatar,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { EGSpeedDial } from "../../../../../../MainComponents/Button/SpeedDial/SpeedDial";
import { BoldJudge, OutlineEdit, TrashCan } from "../../../../../../Icon";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { EGIconButton } from "../../../../../../MainComponents/Button/IconButton/IconButton";
import { EGButton } from "../../../../../../MainComponents/Button/EGButton/EGButton";
import { EGBasicSelect } from "../../../../../../MainComponents/Select/EGBasicSelect";
import {
  getInvestigation,
  listAssignedUsers,
} from "../../../../../../../apis";
import { parseDateToDDMMYYYY2 } from "../../../../../../../helpers/dateTransform";
import useLocalizedLabel from "../../../../../../../hooks/useLocalizedLabel";
import {
  formatWorkdayMessage,
  resolveSeverityLabel,
  useWorkdayCase,
} from "../../../../../../../hooks/useWorkdayCase";
import {
  useReport,
  useReportId,
} from "../../../../../../../context/report/ReportContext";
import { useReportContext } from "../../../../../ReportContext/useReportContext";
import Comments from "../../../../Information/Comments";
import { ActionOrigin, DiscardedProposals, RecordMeta } from "./ResolutionOrigin";
import { groupRecordsByPerson } from "../../../../../../../helpers/correctiveActions";
import { isMultipleSanctionsEnabled } from "../../../../../../../config/features/multipleSanctions";

export const SanctionsAndCompensations = ({
  actions,
  actionsType,
  deleteFunction,
  openModalConfirm,
  closeModalConfirm,
  hdlOpenDialog,
  investigationList = [],
}) => {
  const [t] = useTranslation("report");
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const {
    involvedType,
    sanctionsOptions,
    benefitOptions,
    severityOptions,
    status_locales,
  } = useReportContext();
  const getLocalizedLabel = useLocalizedLabel();
  // Con varios registros por persona se listan juntos para que se lean como uno.
  const multipleEnabled = isMultipleSanctionsEnabled();
  const orderedActions = multipleEnabled ? groupRecordsByPerson(actions) : actions;
  // El envío a Workday (confirmación, llamada a djaguar y avisos) vive en el hook.
  const { sendToWorkday: sendWorkdayCase, isSending } = useWorkdayCase({
    openModalConfirm,
    closeModalConfirm,
  });
  const { state } = useReport();
  const reportDetail = state?.currentReport?.reportDetail;
  const folio = reportDetail?.folio ?? "";
  // La clasificacion llega como nodo de catalogo ({ label, locales })
  const classification = reportDetail?.classification
    ? getLocalizedLabel(reportDetail.classification)
    : "";
  // Siempre se manda: sin definir va explícito como "No definida"
  const severity = resolveSeverityLabel({
    severity: reportDetail?.severity,
    severityOptions,
    getLocalizedLabel,
    undefinedLabel: t("Resolution.workday_severity_undefined"),
  });
  const reportStatus = status_locales[reportDetail?.status?.current]?.label ?? "";
  const reportDate = reportDetail?.created_at
    ? parseDateToDDMMYYYY2(reportDetail.created_at)
    : "";

  const reportId = useReportId();

  // Investigador(es) responsable(s) de la denuncia: los usuarios asignados con
  // el rol `investigation_responsable`, el mismo criterio con el que el folder
  // de la investigación elige a su lead. Solo hace falta para el caso de
  // Workday, así que no se consulta en la pestaña de compensaciones.
  const [responsables, setResponsables] = useState([]);

  useEffect(() => {
    if (actionsType !== "sanctions" || !reportId) return;
    let active = true;
    (async () => {
      try {
        const resp = await listAssignedUsers(reportId);
        if (!active) return;
        setResponsables(
          (resp?.users ?? []).filter((user) =>
            user?.assign_details?.rolesInvestigation?.includes(
              "investigation_responsable"
            )
          )
        );
      } catch (error) {
        // Sin responsables el caso se puede crear igual, solo va sin esa línea.
        console.error("No se pudieron cargar los usuarios asignados", error);
      }
    })();
    return () => {
      active = false;
    };
  }, [actionsType, reportId]);

  // El caso se le asigna a un investigador responsable, así que solo sirven los
  // que tienen SOEID capturado en su perfil.
  const responsablesWithSoeid = responsables.filter((user) => user.soeid);
  // Con más de uno hay que elegir; con uno solo es ese y no se pregunta. La
  // elección es del reporte completo, no de cada sanción.
  const [responsableId, setResponsableId] = useState("");
  const selectedResponsable =
    responsablesWithSoeid.find((user) => user.id === responsableId) ??
    (responsablesWithSoeid.length === 1 ? responsablesWithSoeid[0] : null);

  // Conclusión de la(s) investigación(es) de la denuncia. El listado solo trae
  // alias y título, así que hay que pedir cada investigación; solo se hace en la
  // pestaña de sanciones y una vez por alias.
  const [conclusions, setConclusions] = useState([]);
  const investigationAliases = (investigationList ?? [])
    .map((item) => item?.alias)
    .filter(Boolean)
    .join("|");

  useEffect(() => {
    if (actionsType !== "sanctions" || !investigationAliases) return;
    let active = true;
    (async () => {
      // allSettled: si una investigación falla, las demás conservan su
      // conclusión. Igual que arriba, sin conclusión el caso se crea, solo va
      // sin esa línea.
      const results = await Promise.allSettled(
        investigationAliases
          .split("|")
          // getInvestigation responde envuelta: { message, response, status }
          .map(async (alias) => (await getInvestigation(alias))?.response)
      );
      if (!active) return;
      results
        .filter((result) => result.status === "rejected")
        .forEach((result) =>
          console.error(
            "No se pudo cargar la conclusión de la investigación",
            result.reason
          )
        );
      setConclusions(
        results
          .filter((result) => result.status === "fulfilled")
          .map((result) => result.value?.conclusion)
          .filter(Boolean)
      );
    })();
    return () => {
      active = false;
    };
  }, [actionsType, investigationAliases]);


  // El SOEID del involucrado vive en `additional` (SOEID / GEID / num. de nomina)
  const soeidOf = (action) => String(action?.additional ?? "").trim();

  /**
   * Sanciones confirmadas de ese involucrado, con la misma etiqueta que pinta la
   * lista: las del catalogo (`sanctions`) y las capturadas a mano (`another`).
   *
   * Las descartadas viven aparte en `action.discarded` y NO entran: son las que
   * el resolutor decidio no aplicar.
   */
  const confirmedSanctions = (action) => {
    const fromCatalogue = (action?.[actionsTaken[actionsType].type] ?? []).map(
      (item) =>
        typeof item === "object" && item
          ? getLocalizedLabel(item) || item.label || item.path
          : actionsTaken[actionsType]?.options.find(
              (option) => option.value === item
            )?.label || item
    );

    const freeText = (action?.another ?? []).map(
      (item) => item?.[actionsTaken[actionsType].another]
    );

    return [...fromCatalogue, ...freeText].filter(Boolean).join("; ");
  };

  /**
   * Cuerpo del caso de Workday: folio, clasificacion de la denuncia, los datos
   * del involucrado sancionado y las sanciones que se le van a aplicar.
   *
   * Todo va en `message` porque el puente no acepta un identificador propio de
   * Linea Etica: el folio ahi dentro es lo unico que permite cruzar el caso con
   * la denuncia que lo origino.
   */
  const buildMessage = (action) =>
    formatWorkdayMessage([
      [t("Resolution.workday_label_report"), folio],
      [t("Resolution.workday_label_report_date"), reportDate],
      [t("Resolution.workday_label_classification"), classification],
      [t("Resolution.workday_label_severity"), severity],
      [t("Resolution.workday_label_status"), reportStatus],
      [t("Resolution.workday_label_involved"), action.name],
      [
        t("Resolution.workday_label_role"),
        involvedType[action.kind || action.rol],
      ],
      [t("Resolution.workday_label_soeid"), soeidOf(action)],
      [t("Resolution.job_position"), action.relationship],
      [t("Resolution.area"), action.details],
      [t("Resolution.assigned_sanctions"), confirmedSanctions(action)],
      [
        t("Resolution.workday_label_conclusion", {
          count: conclusions.length,
        }),
        conclusions.join(" | "),
      ],
      [
        t("Resolution.workday_label_responsables", {
          count: responsables.length,
        }),
        responsables.map((user) => user.name).join(", "),
      ],
    ]);

  /**
   * `workday` de cada sanción que djaguar devolvió en esta sesión, por id de
   * sanción. La lista (`actions`) no se vuelve a pedir tras el envío, así que
   * aquí queda lo último que se supo: el caso registrado, la marca de un envío
   * sin confirmar o null si la sanción quedó libre.
   */
  const [registeredWorkday, setRegisteredWorkday] = useState({});
  const workdayOf = (action) =>
    action.id in registeredWorkday
      ? registeredWorkday[action.id]
      : action.workday ?? null;
  const rememberWorkday = (action, workday) =>
    setRegisteredWorkday((prev) => ({ ...prev, [action.id]: workday ?? null }));
  const pendingActions = actions.filter((action) => !workdayOf(action)?.case_id);

  /**
   * djaguar ya registró el caso en la sanción y lo devuelve en `workday`. Si no
   * lo trae es que no pudo guardarlo: el caso igual queda marcado aquí
   * (`unsaved`) para que el botón no permita crear un duplicado.
   */
  const handleCreated = (action, created) => {
    if (created?.workday) {
      rememberWorkday(action, created.workday);
      return;
    }
    const caseId = created?.caseID || created?.case?.caseID || created?.case?.id;
    rememberWorkday(action, {
      case_id: caseId,
      wid: created?.case?.id,
      link: created?.case?.caseLink,
      unsaved: true,
    });
    enqueueSnackbar(t("Resolution.workday_not_registered", { case: caseId }), {
      variant: "error",
      autoHideDuration: 20000,
    });
  };

  /**
   * Crea el caso de Workday: se le ASIGNA al investigador responsable
   * (forWorker) y TRATA SOBRE el involucrado sancionado (aboutWorker).
   */
  const sendToWorkday = (action) =>
    sendWorkdayCase({
      key: action.id,
      reportId,
      sanctionId: action.id,
      soeid: selectedResponsable?.soeid,
      name: selectedResponsable?.name,
      aboutSoeid: soeidOf(action),
      aboutName: action.name,
      title: t("Resolution.workday_case_title", { folio }),
      message: buildMessage(action),
      onCreated: (created) => handleCreated(action, created),
      onWorkday: (workday) => rememberWorkday(action, workday),
    });

  /** "07/10/2026 11:06" en la zona del navegador. */
  const formatSentAt = (value) => {
    const date = new Date(value);
    if (!value || Number.isNaN(date.getTime())) return "";
    const time = date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${parseDateToDDMMYYYY2(value)} ${time}`;
  };

  const actionsTaken = {
    sanctions: {
      type: "sanctions",
      color: "orange",
      colorHover: "orangeII",
      assignedActions: t("Resolution.assigned_sanctions"),
      options: sanctionsOptions,
      another: "another_sanction",
    },
    compensations: {
      type: "benefits",
      color: "vividViolet",
      colorHover: "vividVioletII",
      assignedActions: t("Resolution.benefits_granted"),
      options: benefitOptions,
      another: "another_benefit",
    },
  };

  return (
    <>
    {/* Con varios responsables hay que elegir a cuál se le asigna el caso de
        Workday. La elección es del reporte completo, así que se pregunta una
        vez, fuera de la lista (que hace scroll), y no en cada involucrado.
        Si todos ya tienen caso, no hay nada que asignar. */}
    {actionsType === "sanctions" &&
      responsablesWithSoeid.length > 1 &&
      pendingActions.length > 0 && (
      <Stack direction="row" justifyContent="flex-end" sx={{ px: 1, pb: 1 }}>
        <Box sx={{ minWidth: 240 }}>
          <EGBasicSelect
            label={t("Resolution.workday_assign_to")}
            value={selectedResponsable?.id ?? ""}
            options={responsablesWithSoeid.map((user) => ({
              value: user.id,
              label: `${user.name} (${user.soeid})`,
            }))}
            onChange={(e) => setResponsableId(e.target.value)}
          />
        </Box>
      </Stack>
    )}
    <Box
      px={{ xs: 0.33, md: 1 }}
      maxHeight={"40vh"}
      sx={{ overflowY: "auto" }}
      className="rounded"
    >
      {orderedActions.map((action, i) => (
        <Box
          key={action.id}
          className={`${i < orderedActions.length - 1 ? "border-bottom" : ""}`}
          sx={{
            minHeight: "50px",
            position: "relative",
            my: "auto",
          }}
        >
          <EGSpeedDial
            sx={{
              position: "absolute",
              right: -10,
              top: 1.5,
              scale: 0.8,
              zIndex: 1,
            }}
            tooltipPlacement="top"
            actions={[
              {
                icon: <OutlineEdit color={theme.palette.blue.main} />,
                name: t("Resolution.edit"),
                direction: "top",
                onClick: () =>
                  hdlOpenDialog(actionsTaken[actionsType].type, action),
              },
              {
                icon: <TrashCan color={theme.palette.error.main} />,
                name: t("Resolution.delete"),
                direction: "top",
                onClick: () =>
                  openModalConfirm({
                    variant: "error",
                    title: t("Resolution.attention"),
                    titleColor: theme.palette.pink.main,
                    description1: t(
                      "Care.deleting_this_measure_will_permanently_erase_all_content"
                    ),
                    question: t("Care.do_you_want_continue"),
                    leftButtonText: t("Resolution.delete"),
                    leftButtonColor: "error",
                    leftButtonVariant: "outlined",
                    onClickLeft: () => deleteFunction(action),
                    rightButtonText: t("Care.cancel"),
                    rightButtonColor: "secondary",
                    onClickRight: () => closeModalConfirm(),
                    ethiquin: "megaphone",
                  }),
              },
            ]}
          />

          <ListItem dense sx={{ padding: 0, pb: 0.33 }}>
            <ListItemAvatar>
              <Avatar sx={{ backgroundColor: "transparent" }}>
                <EGIconButton
                  iconName={
                    action.confirm
                      ? "outline_profile_tick"
                      : "outline_profile_remove"
                  }
                  color={
                    action.confirm
                      ? actionsTaken[actionsType].color
                      : "blueGray500"
                  }
                  title={
                    action.confirm
                      ? t("Resolution.confirmed")
                      : t("Resolution.unconfirmed")
                  }
                  colorHover={
                    action.confirm
                      ? actionsTaken[actionsType].colorHover
                      : "blueGray700"
                  }
                />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box
                  display="flex"
                  alignItems="center"
                  flexWrap="wrap"
                  maxWidth={"90%"}
                >
                  <Typography
                    mr={0.5}
                    color="secondary"
                    fontWeight={600}
                    variant="body1"
                    component="span"
                  >
                    {`${involvedType[action.kind]}: `}
                  </Typography>
                  <Typography
                    color={actionsTaken[actionsType].color}
                    variant="body1"
                    component="span"
                    fontWeight={700}
                    maxWidth={"90%"}
                    textOverflow={""}
                  >
                    {action.name}
                  </Typography>
                </Box>
              }
              secondary={
                <Stack
                  spacing={2.5}
                  display="flex"
                  alignItems="center"
                  justifyContent="normal"
                  flexDirection="row"
                  flexWrap="wrap"
                  py={0.5}
                  maxWidth={"90%"}
                  component={"span"}
                >
                  <Typography variant="body2" component="span">
                    {involvedType[action.kind || action.rol]}
                  </Typography>
                  {action.additional && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.employee_number"
                    )}: ${action.additional}`}</Typography>
                  )}
                  {action.relationship && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.job_position"
                    )}: ${action.relationship}`}</Typography>
                  )}
                  {action.details && (
                    <Typography variant="body2" component="span">{`${t(
                      "Resolution.area"
                    )}: ${action.details}`}</Typography>
                  )}
                </Stack>
              }
            />
          </ListItem>

          {multipleEnabled && (
            <Box sx={{ px: 1, pb: 0.5 }}>
              <RecordMeta
                record={action}
                position={
                  orderedActions
                    .filter((item) => item.name === action.name)
                    .indexOf(action) + 1
                }
                total={
                  orderedActions.filter((item) => item.name === action.name)
                    .length
                }
              />
            </Box>
          )}

          <Grid2 container mb={0.5}>
            <Grid2 size={{ xs: 12, md: 6 }}>
              <List
                subheader={<li />}
                dense
                sx={{
                  px: 0.5,
                  maxHeight: "25vh",
                  overflow: "auto",
                  "& ul": { padding: 0 },
                }}
              >
                <li>
                  <ul>
                    <ListSubheader
                      component={"h6"}
                      sx={{
                        px: 0.5,
                        py: 0,
                        lineHeight: "15px",
                        width: "100%",
                      }}
                    >
                      {`${actionsTaken[actionsType].assignedActions}:`}
                    </ListSubheader>
                    {action[actionsTaken[actionsType].type] &&
                      action[actionsTaken[actionsType].type].map((item, i) => (
                        <ListItem sx={{ px: 1, py: 0 }} key={`action-${i}`}>
                          <ListItemIcon sx={{ minWidth: "25px" }}>
                            <BoldJudge
                              color={
                                theme.palette[actionsTaken[actionsType]?.color]
                                  .main
                              }
                              size={20}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              typeof item === "object" && item?.label
                                ? item.label
                                : actionsTaken[actionsType]?.options.find(
                                    (option) => option.value === item
                                  )?.label || item
                            }
                            primaryTypographyProps={{ variant: "body1" }}
                            secondary={
                              <ActionOrigin action={item} />
                            }
                            secondaryTypographyProps={{ component: "div" }}
                          />
                        </ListItem>
                      ))}
                    {action.another &&
                      action.another.map((another) => (
                        <ListItem
                          sx={{ px: 1, py: 0 }}
                          key={`another-${another.id}`}
                        >
                          <ListItemIcon sx={{ minWidth: "25px" }}>
                            <BoldJudge
                              color={
                                theme.palette[actionsTaken[actionsType]?.color]
                                  .main
                              }
                              size={20}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={another[actionsTaken[actionsType].another]}
                            primaryTypographyProps={{ variant: "body1" }}
                            secondary={<ActionOrigin action={another} />}
                            secondaryTypographyProps={{ component: "div" }}
                          />
                        </ListItem>
                      ))}
                  </ul>
                </li>
              </List>
            </Grid2>
            {action.comment && (
              <Grid2 size={{ xs: 12, md: 6 }} px={1}>
                <Typography variant="h6" color="secondary">
                  {`${t("Resolution.details")}:`}
                </Typography>
                <Comments
                  text={action.comment}
                  background="white"
                  padding={1}
                  maxLength={250}
                  border="blueGray50"
                  rounded
                />
              </Grid2>
            )}
          </Grid2>
          <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
            {action.determination_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.determination_date")}: ${
                  action.determination_date
                }`}
              </Typography>
            )}
          </Stack>
          <Stack direction="column" sx={{ px: 1, py: 0.5 }}>
            {action.execution_date && (
              <Typography variant="body1" component="span">
                {`${t("Resolution.execution_date")}: ${action.execution_date}`}
              </Typography>
            )}
          </Stack>

          <DiscardedProposals discarded={action.discarded} />

          {/* Workday: un caso por registro, asi cada equipo manda el suyo aunque
              sancionen a la misma persona. Hacen falta los dos
              SOEID — el del involucrado (aboutWorker) y el del investigador
              responsable al que se le asigna (forWorker) —, así que sin alguno
              el botón queda deshabilitado con el motivo al lado. */}
          {actionsType === "sanctions" &&
            (() => {
              // Ya enviado: en vez del botón, el caso y quién lo mandó. Nunca
              // se ofrece de nuevo, ni siquiera si no quedó guardado en djaguar.
              const workday = workdayOf(action);
              if (workday?.case_id) {
                return (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="flex-end"
                    flexWrap="wrap"
                    spacing={1}
                    sx={{ px: 1, pb: 1 }}
                  >
                    <Typography variant="body2" fontSize={12} fontWeight={600}>
                      {t("Resolution.workday_sent", { case: workday.case_id })}
                    </Typography>
                    {workday.sent_at && (
                      <Typography variant="body2" color="secondary" fontSize={12}>
                        {t("Resolution.workday_sent_detail", {
                          date: formatSentAt(workday.sent_at),
                          user: workday.sent_by?.name || "",
                        })}
                      </Typography>
                    )}
                    {workday.unsaved && (
                      <Typography variant="body2" color="error" fontSize={12}>
                        {t("Resolution.workday_unsaved")}
                      </Typography>
                    )}
                    {workday.link && (
                      <Link
                        href={workday.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        fontSize={12}
                      >
                        {t("Resolution.workday_open_case")}
                      </Link>
                    )}
                  </Stack>
                );
              }

              const canSend = !!soeidOf(action) && !!selectedResponsable;
              const missingReason = !soeidOf(action)
                ? t("Resolution.workday_missing_soeid")
                : !responsablesWithSoeid.length
                ? t("Resolution.workday_missing_responsable")
                : !selectedResponsable
                ? t("Resolution.workday_pick_responsable")
                : "";
              // Marca de djaguar sin case_id: un envío en curso o uno que Workday
              // no confirmó. El botón se queda; djaguar decide (409, o reenviar
              // después de revisar en Workday que el caso no existe).
              const pendingReason =
                workday?.status === "unknown"
                  ? t("Resolution.workday_unconfirmed")
                  : workday?.status === "sending"
                  ? t("Resolution.workday_in_progress")
                  : "";

              return (
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="flex-end"
                  flexWrap="wrap"
                  spacing={1}
                  sx={{ px: 1, pb: 1 }}
                >
                  {pendingReason && (
                    <Typography variant="body2" color="error" fontSize={12}>
                      {pendingReason}
                    </Typography>
                  )}
                  {missingReason && (
                    <Typography variant="body2" color="secondary" fontSize={12}>
                      {missingReason}
                    </Typography>
                  )}
                  <EGButton
                    size="thin"
                    color={canSend ? "success" : "light"}
                    label="Workday"
                    iconName="send"
                    iconColor={canSend ? "white" : "secondary"}
                    iconColorHover="white"
                    iconSize={18}
                    // Sobre el verde de `success` el texto va en blanco; sin los
                    // SOEID el boton queda en `light` y conserva el color.
                    labelColor={canSend ? "white" : ""}
                    labelColorHover="white"
                    loading={isSending(action.id)}
                    disabled={!canSend || isSending(action.id)}
                    onClick={() => sendToWorkday(action)}
                    sx={{ maxWidth: "fit-content" }}
                  />
                </Stack>
              );
            })()}
        </Box>
      ))}
    </Box>
    </>
  );
};
